import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { PageHeader, StatusChip, LoadingBlock } from "@/components/Common";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, KeyRound, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { fmtDate } from "@/lib/utils-app";

const ROLES = ["owner", "manager", "front_desk", "housekeeping", "maintenance"];
const empty = { email: "", password: "", name: "", role: "front_desk" };

export default function Users() {
    const { user: me } = useAuth();
    const isOwner = me?.role === "owner";
    const [items, setItems] = useState(null);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(empty);
    const [pwd, setPwd] = useState({ id: null, password: "" });

    const load = () => api.get("/users").then((r) => setItems(r.data));
    useEffect(() => { load(); }, []);

    const create = async () => {
        if (form.password.length < 6) return toast.error("Password must be 6+ chars");
        try {
            await api.post("/users", form);
            toast.success("User created");
            setForm(empty);
            setOpen(false);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Create failed");
        }
    };

    const patch = async (u, data) => {
        try {
            await api.patch(`/users/${u.id}`, data);
            toast.success("Updated");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Update failed");
        }
    };

    const remove = async (u) => {
        if (!window.confirm(`Delete ${u.email}?`)) return;
        try {
            await api.delete(`/users/${u.id}`);
            toast.success("Deleted");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Delete failed");
        }
    };

    const resetPwd = async () => {
        if (pwd.password.length < 6) return toast.error("Password must be 6+ chars");
        try {
            await api.post(`/users/${pwd.id}/reset-password`, { new_password: pwd.password });
            toast.success("Password reset");
            setPwd({ id: null, password: "" });
        } catch (e) {
            toast.error(e.response?.data?.detail || "Reset failed");
        }
    };

    if (!items) return <LoadingBlock rows={6} />;

    return (
        <>
            <PageHeader
                eyebrow="Access control"
                title="Users"
                subtitle={isOwner ? "Manage staff accounts, roles and access." : "Read-only view. Only owners can create or modify users."}
                actions={isOwner && (
                    <Button onClick={() => setOpen(true)} data-testid="users-new"><Plus className="w-4 h-4 mr-1" /> New user</Button>
                )}
            />
            <Card>
                <CardContent className="p-0">
                    <table className="w-full text-sm" data-testid="users-table">
                        <thead className="bg-secondary text-xs uppercase tracking-wider text-muted-foreground">
                            <tr>
                                <th className="text-left py-2.5 px-4">Name / Email</th>
                                <th className="text-left py-2.5 px-4">Role</th>
                                <th className="text-left py-2.5 px-4">Active</th>
                                <th className="text-left py-2.5 px-4">Created</th>
                                <th className="text-left py-2.5 px-4">Last login</th>
                                {isOwner && <th className="text-right py-2.5 px-4">Actions</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((u) => {
                                const isSelf = u.id === me?.id;
                                return (
                                    <tr key={u.id} className="border-t border-border" data-testid="users-row">
                                        <td className="py-2.5 px-4">
                                            <div className="font-medium">{u.name || "—"} {isSelf && <span className="text-xs text-muted-foreground">(you)</span>}</div>
                                            <div className="text-xs text-muted-foreground">{u.email}</div>
                                        </td>
                                        <td className="py-2.5 px-4">
                                            {isOwner ? (
                                                <Select value={u.role} onValueChange={(v) => patch(u, { role: v })}>
                                                    <SelectTrigger className="h-8 w-36" data-testid="users-role-set"><SelectValue /></SelectTrigger>
                                                    <SelectContent>
                                                        {ROLES.map((r) => <SelectItem key={r} value={r} className="capitalize">{r.replace("_", " ")}</SelectItem>)}
                                                    </SelectContent>
                                                </Select>
                                            ) : <StatusChip status={u.role === "owner" ? "connected" : "available"} label={u.role.replace("_", " ")} />}
                                        </td>
                                        <td className="py-2.5 px-4">
                                            <Switch
                                                checked={u.active}
                                                disabled={!isOwner || isSelf}
                                                onCheckedChange={(v) => patch(u, { active: v })}
                                                data-testid="users-active-toggle"
                                            />
                                        </td>
                                        <td className="py-2.5 px-4 text-xs text-muted-foreground">{fmtDate(u.created_at)}</td>
                                        <td className="py-2.5 px-4 text-xs text-muted-foreground">{u.last_login ? fmtDate(u.last_login) : "—"}</td>
                                        {isOwner && (
                                            <td className="py-2.5 px-4 text-right space-x-1">
                                                <Button size="sm" variant="outline" onClick={() => setPwd({ id: u.id, password: "" })} data-testid="users-reset-pwd">
                                                    <KeyRound className="w-3.5 h-3.5" />
                                                </Button>
                                                {!isSelf && (
                                                    <Button size="sm" variant="outline" onClick={() => remove(u)} data-testid="users-delete">
                                                        <Trash2 className="w-3.5 h-3.5" />
                                                    </Button>
                                                )}
                                            </td>
                                        )}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </CardContent>
            </Card>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent>
                    <DialogHeader><DialogTitle>Create user</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div><Label>Full name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="users-form-name" /></div>
                        <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="users-form-email" /></div>
                        <div><Label>Password</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="users-form-password" /></div>
                        <div>
                            <Label>Role</Label>
                            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                                <SelectTrigger data-testid="users-form-role"><SelectValue /></SelectTrigger>
                                <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r} className="capitalize">{r.replace("_", " ")}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                        <Button onClick={create} data-testid="users-form-create">Create</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={!!pwd.id} onOpenChange={(o) => !o && setPwd({ id: null, password: "" })}>
                <DialogContent>
                    <DialogHeader><DialogTitle>Reset password</DialogTitle></DialogHeader>
                    <div>
                        <Label>New password (min 6 chars)</Label>
                        <Input type="password" value={pwd.password} onChange={(e) => setPwd({ ...pwd, password: e.target.value })} data-testid="users-reset-input" />
                        <div className="text-xs text-muted-foreground mt-2">Tell the user their new password through a secure channel.</div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setPwd({ id: null, password: "" })}>Cancel</Button>
                        <Button onClick={resetPwd} data-testid="users-reset-confirm">Reset</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
